/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import type {Translations} from 'helpers/config'
import type {PackageItem} from 'types/entities/PackageItem'
import type {PackagesItemsPerson, PackagesItemsResponse} from 'types/entities/PackagesItems'
import {PackageItemItemType} from 'types/enums/PackageItemItemType'
import {PackageCategory} from 'types/enums/PackageCategory'

type AddItemCandidate = PackageItem | PackagesItemsPerson
export const CREDIT_GROUP_NAME = 'Credits'
const CREDIT_NAME_PREFIX = 'Credit'

const CREDIT_TIER_AMOUNTS: Record<string, number> = {
    'mini standard': 32,
    'midi standard': 48,
    'maxi standard': 64,
}

const hasTranslationValues = (value: Translations | undefined | null): value is Translations =>
    !!value && Object.values(value).some((entry) => typeof entry === 'string' && entry.trim().length > 0)

const buildTranslationsFromLabel = (label: string): Translations => ({
    en: label,
    de: label,
    nl: label,
})

const resolveAmountFromLabel = (label?: string | null): number | null => {
    if (!label?.trim()) {
        return null
    }

    const knownAmount = CREDIT_TIER_AMOUNTS[label.trim().toLowerCase()]
    if (knownAmount) {
        return knownAmount
    }

    const match = label.match(/(\d+)\s*credits?/i)
    if (match) {
        return Number(match[1])
    }

    return null
}

const getCreditItemAmount = (item: PackageItem): number | null => {
    const packageAmount =
        item.creditPackage?.amount_total ||
        (item.article as { creditPackage?: { amount_total?: number } } | undefined)?.creditPackage
            ?.amount_total

    if (typeof packageAmount === 'number' && packageAmount > 0) {
        return packageAmount
    }

    const articleName = item.article?.article_name
    const fromArticleName = resolveAmountFromLabel(articleName?.en || articleName?.de || null)
    if (fromArticleName !== null) {
        return fromArticleName
    }

    const creditPackageName =
        item.creditPackage?.name ||
        (item.article as { creditPackage?: { name?: string } } | undefined)?.creditPackage?.name
    const fromPackageName = resolveAmountFromLabel(creditPackageName)
    if (fromPackageName !== null) {
        return fromPackageName
    }

    return resolveAmountFromLabel(item.name?.en || item.name?.de || null)
}

const buildCreditLabel = (name: string, amount: number | null): string => {
    const cleanedName = name.replace(/\s*\(\d+\s*credits?\)\s*$/i, '').trim() || name

    if (amount === null) {
        return `${CREDIT_NAME_PREFIX} ${cleanedName}`
    }

    return `${CREDIT_NAME_PREFIX} ${cleanedName} (${amount} credits)`
}

export const getCreditItemDisplayName = (item: PackageItem): Translations => {
    const creditAmount = getCreditItemAmount(item)
    const articleName = item.article?.article_name
    if (hasTranslationValues(articleName) && !isGenericCreditLabel(articleName)) {
        return buildTranslationsFromLabel(
            buildCreditLabel(articleName.en || articleName.de || 'Standard', creditAmount)
        )
    }

    const creditPackageName =
        item.creditPackage?.name ||
        (item.article as { creditPackage?: { name?: string } } | undefined)?.creditPackage?.name

    if (creditPackageName) {
        return buildTranslationsFromLabel(buildCreditLabel(creditPackageName, creditAmount))
    }

    if (hasTranslationValues(item.name) && !isGenericCreditLabel(item.name)) {
        return buildTranslationsFromLabel(
            buildCreditLabel(item.name.en || item.name.de || 'Standard', creditAmount)
        )
    }

    return buildTranslationsFromLabel(buildCreditLabel('Standard', creditAmount))
}

const isGenericCreditLabel = (value: Translations): boolean => {
    const normalized = Object.values(value)
        .filter((entry): entry is string => typeof entry === 'string')
        .map((entry) => entry.trim().toLowerCase())

    return normalized.length > 0 && normalized.every((entry) => entry === 'credit')
}

export const isCreditPackageItem = (item: AddItemCandidate): boolean => {
    return (
        (item.type === PackageItemItemType.Credit ||
            !!(item as PackageItem).creditPackage ||
            !!(item as PackageItem).article?.creditPackage ||
            (item as PackageItem).extra?.groupName === CREDIT_GROUP_NAME)
    )
}

const getCreditPackageLabels = (item: PackageItem): string[] => {
    const nestedCreditPackage = (
        item.article as {creditPackage?: {name?: string}} | undefined
    )?.creditPackage

    return [
        item.creditPackage?.name,
        nestedCreditPackage?.name,
        ...Object.values(item.article?.article_name ?? {}),
        ...Object.values(item.name ?? {}),
    ].filter((label): label is string => typeof label === 'string' && label.trim().length > 0)
}

/** Family credit packs are sold once per group, not once per participant. */
export const isSingleUnitCreditPackage = (item: AddItemCandidate): boolean => {
    if (!isCreditPackageItem(item)) {
        return false
    }

    const packageItem = item as PackageItem
    const category = packageItem.creditPackage?.category || packageItem.article?.creditPackage?.category
    if (category) {
        return category === 'family'
    }
    return getCreditPackageLabels(packageItem).some((label) => {
        const normalized = label.trim().toLowerCase()
        return normalized.includes('family')
    })
}

/** HappyKids uses shared Family packs; other known booking categories use Standard. */
export const matchesCreditCategory = (item: AddItemCandidate, category?: string | null): boolean => {
    if (!category || !isCreditPackageItem(item)) return true
    const usesFamily = category === PackageCategory.HappyKids || category === PackageCategory.HappyKidsPlus
    const packageItem = item as PackageItem
    const creditCategory = packageItem.creditPackage?.category || packageItem.article?.creditPackage?.category
    if (creditCategory) return creditCategory === (usesFamily ? 'family' : 'standard')
    // Compatibility with older catalog entries lacking category metadata.
    return usesFamily === isSingleUnitCreditPackage(item)
}

export const normalizeCreditItem = (item: PackageItem, index: number): PackageItem => {
    const creditPackage =
        item.creditPackage ||
        (item.article as { creditPackage?: PackageItem['creditPackage'] } | undefined)?.creditPackage ||
        null
    const displayName = getCreditItemDisplayName({ ...item, creditPackage })

    return {
        ...item,
        creditPackage,
        name: displayName,
        description: hasTranslationValues(item.description) ? item.description : displayName,
        description_html: hasTranslationValues(item.description_html)
            ? item.description_html
            : displayName,
        extra: {
            ...item.extra,
            groupName: CREDIT_GROUP_NAME,
            subGroupName: null,
        },
        // Keep credits after Eintritt/tickets; index preserves relative credit order.
        position: typeof item.position === 'number' ? item.position : 10_000 + index,
    }
}

const getItemPosition = (item: AddItemCandidate): number | null => {
    if ('position' in item && typeof item.position === 'number') {
        return item.position
    }

    return null
}

export const sortItemsByPosition = <T extends AddItemCandidate>(items: T[]): T[] => {
    return [...items].sort((a, b) => {
        const aPosition = getItemPosition(a)
        const bPosition = getItemPosition(b)

        if (aPosition === null && bPosition === null) return 0
        if (aPosition === null) return 1
        if (bPosition === null) return -1

        return aPosition - bPosition
    })
}

export const getCreditAndUpsellItems = (
    availablePackageItems: PackagesItemsResponse | null | undefined,
    category?: string | null
): PackageItem[] => {
    const creditItems = sortItemsByPosition(availablePackageItems?.creditItems || []).map(
        normalizeCreditItem
    )
    const upsells = sortItemsByPosition(availablePackageItems?.upsells || [])

    // Tickets/Eintritt live in upsells; credits must follow them in picker + cart grouping.
    const creditIds = new Set(creditItems.map((item) => item.id))
    return [...upsells.filter((item) => !creditIds.has(item.id)), ...creditItems]
        .filter((item) => matchesCreditCategory(item, category))
}

export const hasCreditOrUpsellItems = (
    availablePackageItems: PackagesItemsResponse | null | undefined
): boolean => getCreditAndUpsellItems(availablePackageItems).length > 0

export const getAddItemToCalculationItems = (
    availablePackageItems: PackagesItemsResponse | null | undefined,
    category?: string | null
): AddItemCandidate[] => {
    const adultItems = availablePackageItems?.packageItems?.adult || []
    const childItems = availablePackageItems?.packageItems?.child || []

    return [...adultItems, ...childItems, ...getCreditAndUpsellItems(availablePackageItems, category)]
}

export const foodGroups = ['Apero and Snacks', 'Warmes Essen', 'Sonstiges'] as const

const normalizeFoodGroupName = (groupName: string): string =>
    groupName
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .replace(/\s+/g, ' ')

/** Food/gastro upsell groups — EN + DE aliases (UI shows e.g. "APÉRO UND SNACKS"). */
export const isFoodGroup = (groupName?: string | null): boolean => {
    if (!groupName?.trim()) {
        return false
    }

    const normalized = normalizeFoodGroupName(groupName)
    if (foodGroups.some((name) => normalizeFoodGroupName(name) === normalized)) {
        return true
    }

    return (
        normalized.includes('apero') ||
        normalized.includes('snack') ||
        normalized === 'warmes essen' ||
        normalized === 'sonstiges'
    )
}

export const getDefaultAddItemQuantity = (
    item: AddItemCandidate | null | undefined,
    personsCount: number
): number => {
    const safePersons = Math.max(1, personsCount || 1)
    if (!item) {
        return 1
    }

    if (isCreditPackageItem(item)) {
        return isSingleUnitCreditPackage(item) ? 1 : safePersons
    }

    if ('extra' in item && (item as unknown as PackageItem).extra?.groupName) {
        const groupName = (item as unknown as PackageItem).extra.groupName
        if (isFoodGroup(groupName)) {
            return safePersons
        }
        if (groupName === 'Competitions') {
            return 1
        }
    }

    if ('minimum_persons_count' in item && typeof item.minimum_persons_count === 'number') {
        return Math.max(1, item.minimum_persons_count || 1)
    }

    return 1
}

/** Prefer the requested opportunity participants over the package cart's minimum/setup count. */
export const getAddItemPersonsCount = (
    opportunityPersonsCount?: number | null,
    calculationPersonsCount?: number | null
): number => {
    const requestedCount = Number(opportunityPersonsCount ?? 0)
    if (Number.isFinite(requestedCount) && requestedCount > 0) {
        return requestedCount
    }

    const cartCount = Number(calculationPersonsCount ?? 0)
    return Number.isFinite(cartCount) && cartCount > 0 ? cartCount : 0
}
